import { datetimeLocalToEpoch, epochToDatetimeLocal } from 'utils/dates/date.utils';
import FloodSettingsField from '../../../components/FloodSettingsField';
import { donorDateFields, FIELD_HELP, monthlyFields } from '../config/floodSimConfig';
import type { DonorSource, SimMonthlySource, SimSettings } from '../FloodSimSettings.types';
import SettingsSection from './SettingsSection';

interface DonorModeSectionProps {
  sim: SimSettings | null | undefined;
  set: <K extends keyof SimSettings>(field: K, value: SimSettings[K]) => void;
  setMonthlySource: (field: keyof SimMonthlySource, value: number | null) => void;
}

export default function DonorModeSection({ sim, set, setMonthlySource }: DonorModeSectionProps) {
  return (
    <SettingsSection
      title="Donor data source"
      description="Replay readings from an archive, custom table, or generated source."
    >
      {/* Donor Source */}
      <FloodSettingsField
        variant="select"
        label="Donor Source"
        help={FIELD_HELP.donor_source}
        value={sim?.donor_source ?? 'generator'}
        onChange={(value) => set('donor_source', value as DonorSource)}
        isSearchable={false}
        options={[
          { value: 'archive', label: 'Archive' },
          { value: 'custom_table', label: 'Custom table' },
          { value: 'generator', label: 'Generator' },
        ]}
      />

      {sim?.donor_source === 'custom_table' && (
        <FloodSettingsField
          label="Source Table"
          help={FIELD_HELP.donor_source_table}
          type="text"
          value={sim?.donor_source_table ?? ''}
          onChange={(e) => set('donor_source_table', e.target.value || null)}
        />
      )}

      {/* Donor UID */}
      <FloodSettingsField
        label="Donor UID"
        help={FIELD_HELP.donor_uid}
        type="number"
        value={sim?.donor_uid ?? ''}
        onChange={(e) => set('donor_uid', e.target.value === '' ? null : parseInt(e.target.value))}
      />

      <div className="text-theme-muted small">
        Set a fixed replay window <em>or</em> a monthly source — monthly source takes precedence.
      </div>

      {/* Fixed Replay Window */}
      {donorDateFields.map(({ label, field, help }) => (
        <FloodSettingsField
          key={field}
          label={label}
          help={help}
          type="datetime-local"
          value={epochToDatetimeLocal(sim?.[field])}
          onChange={(e) => set(field, datetimeLocalToEpoch(e.target.value))}
        />
      ))}

      {/* Monthly Source */}
      {monthlyFields.map(({ label, field, min, max, placeholder, help }) => (
        <FloodSettingsField
          key={field}
          label={label}
          help={help}
          type="number"
          min={min}
          max={max}
          placeholder={placeholder}
          value={sim?.monthly_source?.[field] ?? ''}
          onChange={(e) =>
            setMonthlySource(field, e.target.value === '' ? null : parseInt(e.target.value))
          }
        />
      ))}
    </SettingsSection>
  );
}
